"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Section, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Button } from "@/src/components";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsTooltip() {
  return (
    <>
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Tooltip
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A popup that displays information related to an element when it receives keyboard focus or mouse hover.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/tooltip" />
        </SectionCard>
      </Section>

      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Example</CardTitle>
              <CardDescription>Hover to see tooltips</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline">Hover me</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This is a tooltip</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline">With delay</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Tooltip with custom delay</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </SectionCard>
      </Section>

      <Section>
        <SectionCard
          header={<CardTitle>Components</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Component</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">TooltipProvider</td>
                <td className="py-2 text-muted-foreground">Context provider (wrap app or section)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">Tooltip</td>
                <td className="py-2 text-muted-foreground">Root component for each tooltip</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">TooltipTrigger</td>
                <td className="py-2 text-muted-foreground">Element that triggers the tooltip</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">TooltipContent</td>
                <td className="py-2 text-muted-foreground">The tooltip content</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>

      <Section>
        <SectionCard
          header={<CardTitle>Accessibility</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Shows on hover and keyboard focus</li>
            <li>• Dismisses with Escape key</li>
            <li>• Has role="tooltip" for screen readers</li>
            <li>• Content is accessible via aria-describedby</li>
          </ul>
        </SectionCard>
      </Section>
    </>
  );
}
